// ============================================================================
// CONTACT TYPES ADMIN PAGE — Gestión de tipos de contacto
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Save, X, Loader2, Building2, AlertCircle, RefreshCw, Phone, PhoneOff, PhoneMissed, Calendar, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { contactTypesApi, type ContactType, type ContactTypeSiteAssignment } from '../../api/contact-types';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';

const PAGE_SIZE = 20;

interface FormData {
  code: string;
  name: string;
  requiresSchedule: boolean;
  triggersRejection: boolean;
  isNoAnswer: boolean;
  sortOrder: number;
}

function TypeBadge({ type }: { type: ContactType }) {
  if (type.triggersRejection) return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">
      <PhoneOff className="h-3 w-3" /> Rechaza presupuesto
    </span>
  );
  if (type.isNoAnswer) return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium">
      <PhoneMissed className="h-3 w-3" /> No contesta
    </span>
  );
  if (type.requiresSchedule) return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
      <Calendar className="h-3 w-3" /> Programar llamada
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
      <MessageSquare className="h-3 w-3" /> Mensaje libre
    </span>
  );
}

export function ContactTypesAdminPage() {
  const toast = useToast();
  const [items, setItems] = useState<ContactType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ContactType | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean; item: ContactType | null; action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [assignmentModal, setAssignmentModal] = useState<{
    isOpen: boolean; item: ContactType | null; sites: ContactTypeSiteAssignment[];
    selectedIds: string[]; isLoading: boolean; isSaving: boolean;
  }>({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false });
  const [bulkModal, setBulkModal] = useState<{
    isOpen: boolean; sites: { id: string; code: string; name: string }[];
    selectedSiteId: string; allTypes: ContactType[];
    selectedTypeIds: string[]; isLoadingSites: boolean; isLoadingTypes: boolean; isSaving: boolean;
  }>({ isOpen: false, sites: [], selectedSiteId: '', allTypes: [], selectedTypeIds: [], isLoadingSites: false, isLoadingTypes: false, isSaving: false });

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm<FormData>({
    defaultValues: { code: '', name: '', requiresSchedule: false, triggersRejection: false, isNoAnswer: false, sortOrder: 0 },
  });


  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await contactTypesApi.getAll(showInactive);
      setItems(data);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudieron cargar los tipos de contacto');
    } finally {
      setIsLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = items.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    i.code.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => {
    setEditingItem(null);
    reset({ code: '', name: '', requiresSchedule: false, triggersRejection: false, isNoAnswer: false, sortOrder: 0 });
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEdit = (item: ContactType) => {
    setEditingItem(item);
    reset({ code: item.code, name: item.name, requiresSchedule: item.requiresSchedule, triggersRejection: item.triggersRejection, isNoAnswer: item.isNoAnswer, sortOrder: item.sortOrder });
    setFormError(null);
    setIsModalOpen(true);
  };

  const handleSave = async (data: FormData) => {
    setIsSaving(true);
    setFormError(null);
    try {
      if (editingItem) {
        await contactTypesApi.update(editingItem.id, data);
        toast.success('Guardado', 'Tipo de contacto actualizado');
      } else {
        await contactTypesApi.create(data);
        toast.success('Creado', 'Tipo de contacto creado correctamente');
      }
      setIsModalOpen(false);
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = (item: ContactType) => {
    setConfirmDialog({ isOpen: true, item, action: item.isActive ? 'deactivate' : 'activate' });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await contactTypesApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivado', `"${confirmDialog.item.name}" desactivado`);
      } else {
        await contactTypesApi.reactivate(confirmDialog.item.id);
        toast.success('Activado', `"${confirmDialog.item.name}" reactivado`);
      }
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenAssignment = async (item: ContactType) => {
    setAssignmentModal({ isOpen: true, item, sites: [], selectedIds: [], isLoading: true, isSaving: false });
    try {
      const sites = await contactTypesApi.getSiteAssignments(item.id);
      setAssignmentModal(prev => ({
        ...prev, sites, selectedIds: sites.filter(s => s.isAssigned).map(s => s.id), isLoading: false,
      }));
    } catch {
      setAssignmentModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSaveAssignment = async () => {
    if (!assignmentModal.item) return;
    setAssignmentModal(prev => ({ ...prev, isSaving: true }));
    try {
      await contactTypesApi.updateSiteAssignments(assignmentModal.item.id, assignmentModal.selectedIds);
      toast.success('Guardado', 'Asignación de centros actualizada');
      setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false });
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo guardar la asignación');
      setAssignmentModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  const handleOpenBulkModal = async () => {
    setBulkModal(prev => ({ ...prev, isOpen: true, isLoadingSites: true }));
    try {
      const token = localStorage.getItem('imi_access_token') || '';
      const r = await fetch('/api/v1/sites/select', { headers: { Authorization: 'Bearer ' + token } });
      const sites = r.ok ? await r.json() : [];
      const allTypes = await contactTypesApi.getAll(false);
      const firstSiteId = sites[0]?.id || '';
      let selectedTypeIds: string[] = [];
      if (firstSiteId) selectedTypeIds = await contactTypesApi.getAssignedTypeIds(firstSiteId);
      setBulkModal(prev => ({ ...prev, sites, allTypes, isLoadingSites: false, selectedSiteId: firstSiteId, selectedTypeIds }));
    } catch {
      toast.error('Error', 'No se pudieron cargar los datos');
      setBulkModal(prev => ({ ...prev, isLoadingSites: false, isOpen: false }));
    }
  };

  const handleBulkSiteChange = async (siteId: string) => {
    setBulkModal(prev => ({ ...prev, selectedSiteId: siteId, isLoadingTypes: true }));
    try {
      const ids = await contactTypesApi.getAssignedTypeIds(siteId);
      setBulkModal(prev => ({ ...prev, selectedTypeIds: ids, isLoadingTypes: false }));
    } catch {
      setBulkModal(prev => ({ ...prev, isLoadingTypes: false }));
    }
  };

  const handleBulkSave = async () => {
    if (!bulkModal.selectedSiteId) return;
    setBulkModal(prev => ({ ...prev, isSaving: true }));
    try {
      await contactTypesApi.bulkAssignToSite(bulkModal.selectedSiteId, bulkModal.selectedTypeIds);
      await loadData();
      setBulkModal({ isOpen: false, sites: [], selectedSiteId: '', allTypes: [], selectedTypeIds: [], isLoadingSites: false, isLoadingTypes: false, isSaving: false });
      toast.success('Guardado', 'Asignación masiva actualizada');
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo guardar la asignación');
      setBulkModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tipos de Contacto</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestiona los resultados posibles al contactar con un paciente durante el seguimiento de presupuestos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleOpenBulkModal}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
            <Building2 className="h-4 w-4" />
            Asignar a centros
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl transition-colors">
            <Plus className="h-4 w-4" />
            Nuevo tipo
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
        <div className="relative flex-1 max-w-xs">
          <input type="text" placeholder="Buscar..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-4 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer ml-auto">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
            className="rounded border-gray-300 text-blue-600" />
          Mostrar inactivos
        </label>
        <button onClick={loadData} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
          </div>
        ) : paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Phone className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">No hay tipos de contacto</p>
            <p className="text-xs mt-1">Crea el primero con el botón "Nuevo tipo"</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Comportamiento</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Centros</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.map(item => (
                  <tr key={item.id} className={`hover:bg-gray-50 transition-colors ${!item.isActive ? 'opacity-50' : ''}`}>
                    <td className="px-6 py-4 font-mono text-xs text-gray-600">{item.code}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">{item.name}</td>
                    <td className="px-6 py-4"><TypeBadge type={item} /></td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleOpenAssignment(item)}
                        className="text-xs text-[#008DD0] hover:underline flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {item.sitesCount ?? 0} centro{item.sitesCount !== 1 ? 's' : ''}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(item)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar">
                          <Save className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleToggleActive(item)}
                          className={`p-1.5 rounded-lg transition-colors ${item.isActive ? 'text-gray-400 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
                          title={item.isActive ? 'Desactivar' : 'Activar'}>
                          {item.isActive ? <X className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(page - 1) * PAGE_SIZE + 1} a {Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length}
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(p => p - 1)} disabled={page === 1} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm text-gray-700">Página {page} de {totalPages}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal crear/editar */}
      <Modal isOpen={isModalOpen} onClose={() => !isSaving && setIsModalOpen(false)}
        title={editingItem ? 'Editar tipo de contacto' : 'Nuevo tipo de contacto'}
        subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Define el comportamiento del tipo de contacto'}>
        <form onSubmit={handleSubmit(handleSave)}>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 flex-1">{formError}</p>
              <button type="button" onClick={() => setFormError(null)} className="p-1 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
            </div>
          )}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                <input type="text" maxLength={20}
                  {...register('code', { required: 'El código es obligatorio', maxLength: { value: 20, message: 'Máximo 20 caracteres' } })}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${errors.code ? 'border-red-300' : 'border-gray-200'}`}
                  placeholder="LLAMADA" />
                {errors.code && <p className="mt-1 text-xs text-red-500">{errors.code.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Orden</label>
                <input type="number" min={0}
                  {...register('sortOrder', { valueAsNumber: true })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input type="text" maxLength={100}
                {...register('name', { required: 'El nombre es obligatorio', maxLength: { value: 100, message: 'Máximo 100 caracteres' } })}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.name ? 'border-red-300' : 'border-gray-200'}`}
                placeholder="Paciente no contesta" />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
            </div>
            <div className="space-y-2 pt-1">
              <p className="text-sm font-medium text-gray-700">Comportamiento</p>
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                <input type="checkbox" {...register('requiresSchedule')}
                  onChange={e => { setValue('requiresSchedule', e.target.checked); if (e.target.checked) { setValue('triggersRejection', false); setValue('isNoAnswer', false); } }}
                  className="rounded border-gray-300 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-800">Programar llamada</p>
                  <p className="text-xs text-gray-500">El usuario deberá indicar fecha y hora para llamar más tarde</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                <input type="checkbox" {...register('isNoAnswer')}
                  onChange={e => { setValue('isNoAnswer', e.target.checked); if (e.target.checked) { setValue('triggersRejection', false); setValue('requiresSchedule', false); } }}
                  className="rounded border-gray-300 text-orange-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-800">No contesta</p>
                  <p className="text-xs text-gray-500">El paciente no respondió la llamada</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-red-200 rounded-xl hover:bg-red-50">
                <input type="checkbox" {...register('triggersRejection')}
                  onChange={e => { setValue('triggersRejection', e.target.checked); if (e.target.checked) { setValue('requiresSchedule', false); setValue('isNoAnswer', false); } }}
                  className="rounded border-gray-300 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Rechaza el presupuesto</p>
                  <p className="text-xs text-red-600">Al seleccionar este tipo, el presupuesto quedará rechazado automáticamente</p>
                </div>
              </label>
            </div>
          </div>
          <ModalFooter>
            <button type="button" onClick={() => !isSaving && setIsModalOpen(false)} disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              <X className="h-4 w-4" /> Cancelar
            </button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl disabled:opacity-50 flex items-center gap-2">
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />{editingItem ? 'Guardar' : 'Crear'}</>}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal asignación individual */}
      <Modal isOpen={assignmentModal.isOpen}
        onClose={() => !assignmentModal.isSaving && setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false })}
        title="Centros asignados"
        subtitle={assignmentModal.item ? `"${assignmentModal.item.name}" estará disponible en los centros seleccionados` : ''}>
        {assignmentModal.isLoading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-300" /></div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {assignmentModal.sites.map(site => (
              <label key={site.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                <input type="checkbox" checked={assignmentModal.selectedIds.includes(site.id)}
                  onChange={() => setAssignmentModal(prev => ({
                    ...prev,
                    selectedIds: prev.selectedIds.includes(site.id)
                      ? prev.selectedIds.filter(id => id !== site.id)
                      : [...prev.selectedIds, site.id],
                  }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="font-medium text-gray-800 text-sm flex-1 truncate">{site.name}</span>
                <span className="text-xs text-gray-400 font-mono shrink-0">{site.code}</span>
                {assignmentModal.selectedIds.includes(site.id) && <span className="text-xs text-blue-600 font-medium shrink-0">Asignado</span>}
              </label>
            ))}
          </div>
        )}
        <ModalFooter>
          <button type="button" onClick={() => setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false })}
            disabled={assignmentModal.isSaving}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
            <X className="h-4 w-4" /> Cancelar
          </button>
          <button type="button" onClick={handleSaveAssignment} disabled={assignmentModal.isSaving || assignmentModal.isLoading}
            className="px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl disabled:opacity-50 flex items-center gap-2">
            {assignmentModal.isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar</>}
          </button>
        </ModalFooter>
      </Modal>

      {/* Modal asignación masiva */}
      <Modal isOpen={bulkModal.isOpen}
        onClose={() => !bulkModal.isSaving && setBulkModal(prev => ({ ...prev, isOpen: false }))}
        title="Asignar tipos a un centro"
        subtitle="Selecciona un centro y marca los tipos de contacto disponibles">
        {bulkModal.isLoadingSites ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-300" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centro</label>
              <select value={bulkModal.selectedSiteId} onChange={e => handleBulkSiteChange(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {bulkModal.sites.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Tipos asignados</label>
                {bulkModal.selectedTypeIds.length > 0 && (
                  <span className="text-xs text-blue-600">({bulkModal.selectedTypeIds.length} seleccionados)</span>
                )}
                <button type="button" onClick={() => setBulkModal(prev => ({
                  ...prev,
                  selectedTypeIds: prev.selectedTypeIds.length === prev.allTypes.length ? [] : prev.allTypes.map(t => t.id),
                }))} className="text-xs text-gray-500 hover:text-gray-700 ml-2">
                  {bulkModal.selectedTypeIds.length === bulkModal.allTypes.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                </button>
              </div>
              {bulkModal.isLoadingTypes ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {bulkModal.allTypes.map(type => (
                    <label key={type.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                      <input type="checkbox" checked={bulkModal.selectedTypeIds.includes(type.id)}
                        onChange={() => setBulkModal(prev => ({
                          ...prev,
                          selectedTypeIds: prev.selectedTypeIds.includes(type.id)
                            ? prev.selectedTypeIds.filter(id => id !== type.id)
                            : [...prev.selectedTypeIds, type.id],
                        }))}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                      <span className="text-sm font-medium text-gray-800 flex-1">{type.name}</span>
                      <TypeBadge type={type} />
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <ModalFooter>
          <button type="button" onClick={() => setBulkModal(prev => ({ ...prev, isOpen: false }))} disabled={bulkModal.isSaving}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
            <X className="h-4 w-4" /> Cancelar
          </button>
          <button type="button" onClick={handleBulkSave}
            disabled={bulkModal.isSaving || bulkModal.isLoadingSites || bulkModal.isLoadingTypes}
            className="px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl disabled:opacity-50 flex items-center gap-2">
            {bulkModal.isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar asignación</>}
          </button>
        </ModalFooter>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })}
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={confirmDialog.action === 'deactivate' ? '¿Desactivar tipo?' : '¿Activar tipo?'}
        message={confirmDialog.action === 'deactivate' ? `"${confirmDialog.item?.name}" será desactivado.` : `"${confirmDialog.item?.name}" será reactivado.`}
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default ContactTypesAdminPage;
